import { weightToLength, initProcess, ProcessConfig, ConversionResults, ParsedVariables } from './index'
import { FTProcess } from '@/models/FTProcess';
import { ConversionDetails } from '@/models/ConversionDetails';
import { convertStep } from './CConvertStep';
import _ from 'lodash';
import { compareStringIgnoreCase } from '@/utils';
import { FTProcessRow } from '@/models/FTProcessRow';

export function loadConfig({ processRow }: ParsedVariables): ProcessConfig {
  return {
    inputs: {
      goods: [
        {
          goodsType: '',
          goodsFilter: {
            form: ['roll']
          },
          multiple: false,
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Input Quantity',
              type: 'quantity',
              value: 'quantity',
              enableConvertFnIf: compareStringIgnoreCase(_.get(processRow, 'goodsObject.rawMaterialUnit', ''),'SQM'),
              disabled: false
            }
          ]
        }
      ]
    },
    outputs: {
      type: 'multiple',
      arguments: [
        {
          name: 'Pcs per Roll',
          type: 'label',
          value: _.get(processRow, 'ftProcess.ftFactoryTicket.order.product.packingMode.0.quantity', ''),
          disabled: true
        },
        {
          name: 'Core ID',
          type: 'label',
          value: _.get(processRow, 'ftProcess.ftFactoryTicket.order.product.size.CORE_ID', ''),
          disabled: true
        },
        {
          name: 'Width',
          type: 'dimension',
          value: 'width',
          unit: 'mm',
          disablePrint: true,
          disabled: false
        },
        {
          name: 'Output Quantity',
          type: 'label',
          value: compareStringIgnoreCase(_.get(processRow, 'goodsObject.rawMaterialUnit', ''),'SQM') ? (_.get(processRow, 'quantity', 0) * 1000)/_.get(processRow, 'goodsObject.density', 0) : _.get(processRow, 'quantity', ''),
          unit: _.get(processRow, 'goodsObject.rawMaterialUnit', ''),
          disablePrint: true,
          disabled: true
        },
        {
          name: 'No of Pieces',
          type: 'label',
          value: _.get(processRow, 'goodsObject.perKgRatio.plA.value', 0) * _.get(processRow, 'quantity', 0),
          disabled: true
        }
      ]
    },
    tableHeaders: [
      {
        text: 'Nominal Speed',
        value: 'nominalSpeed'
      },
      {
        text: 'Planned Hours(s)',
        value: 'plannedHours'
      },
      {
        text: 'Linear Length',
        value: 'linearMeters'
      },
      {
        text: 'No of Labels',
        value: 'numberOfPieces'
      },
      {
        text: 'Expected Output Waste',
        value: 'wastePercentage'
      },
      {
        text: 'Expected Output Waste Weight',
        value: 'waste'
      },
      {
        text: 'Expected Output Net Weight',
        value: 'netWeight'
      }
    ]
  }
}

export function convert(process: FTProcess) : ConversionResults {
  const conversionResult : ConversionResults = {
    inputs: [],
    outputs: [],
    details: new ConversionDetails(),
  };

  initProcess(process);

  let totalInputQuantity = 0;
  let linearLength = 0;
  let noOfPieces = 0;
  let noOfOutputRollsInParallel = 0;
  let totalWidth = 0;
  let netWeight = 0;
  conversionResult.details.totalQuantity = 0;
  conversionResult.outputs = convertStep.slitting(process.inputs[0]);
  const processInputRow = process.inputs[0];
  linearLength += weightToLength(processInputRow.quantity, processInputRow.goodsObject.density, _.get(processInputRow, 'goodsObject.dimensions.width', 0));
  totalInputQuantity += processInputRow.quantity;
  const noOfLabels = _.get(processInputRow, 'goodsObject.perKgRatio.plA.value', 0) * totalInputQuantity;
  const labelsPerRoll = process.ftFactoryTicket.order.product.packingMode[0].quantity;
  _.forEach(conversionResult.outputs, (outputFTProcessRow: FTProcessRow) => {
    totalWidth += outputFTProcessRow.dimensions.width || 0;
  });
  if(processInputRow.goodsObject.dimensions) {
    noOfOutputRollsInParallel = Math.floor(processInputRow.goodsObject.dimensions.width / totalWidth);
    const removedLinerPSLWidth = processInputRow.goodsObject.dimensions.width - (totalWidth * noOfOutputRollsInParallel);
    const removedWasteQuantity = (processInputRow.goodsObject.psl.linerGsm * (removedLinerPSLWidth/1000) * linearLength)/1000;
    netWeight = totalInputQuantity - removedWasteQuantity;
  }
  _.forEach(conversionResult.outputs, (outputFTProcessRow: FTProcessRow) => {
    if(outputFTProcessRow.goodsObject.perKgRatio.plA && outputFTProcessRow.goodsObject.perKgRatio.plA.value) {
      outputFTProcessRow.quantity = (outputFTProcessRow.dimensions.width / totalWidth) * netWeight;
      outputFTProcessRow.goodsObject.setPerKgRatio('plA', noOfLabels/outputFTProcessRow.quantity);
      const currentNoOfRolls = noOfLabels / labelsPerRoll;
      outputFTProcessRow.goodsObject.setPerKgRatio('plB', currentNoOfRolls/outputFTProcessRow.quantity)
      noOfPieces += noOfLabels;
    } else {
      outputFTProcessRow.quantity = (outputFTProcessRow.goodsObject.density * linearLength * (outputFTProcessRow.dimensions.width * noOfOutputRollsInParallel / 1000)) / 1000
    }
  });
  conversionResult.details.numberOfPieces = noOfPieces;
  conversionResult.details.linearMeters = linearLength;
  conversionResult.details.waste = totalInputQuantity / netWeight;
  conversionResult.details.wastePercentage = totalInputQuantity ? (conversionResult.details.waste / totalInputQuantity) * 100 : 0;
  conversionResult.details.netWeight = netWeight;
  conversionResult.details.kgPerHour = 60 * process.machineDetail.mechanicalSpeed * (conversionResult.outputs[0].dimensions.width / 1000) * conversionResult.outputs[0].goodsObject.density;
  conversionResult.details.plannedHours = (conversionResult.details.totalQuantity + conversionResult.details.waste) / conversionResult.details.kgPerHour;

  return conversionResult;
}